import { Injectable, inject } from '@angular/core';
import { ActivatedRouteSnapshot, CanActivate, Router, RouterStateSnapshot, UrlTree } from '@angular/router';
import { Observable, from, of } from 'rxjs';
import { map, catchError, switchMap } from 'rxjs/operators';
import Keycloak from 'keycloak-js';
import { UserService } from '../providers/user.service';
import { RolePermissionService } from '../providers/role-permission.service';
import { NativeAuthService } from '../providers/native-auth.service';
import { UserRole } from '../enums/user-role.enum';

@Injectable({
  providedIn: 'root'
})
export class AuthGuard implements CanActivate {
  private readonly keycloak = inject(Keycloak, { optional: true });

  constructor(
    private userService: UserService,
    private router: Router,
    private rolePermission: RolePermissionService,
    private nativeAuthService: NativeAuthService
  ) {}

  canActivate(
    route: ActivatedRouteSnapshot,
    state: RouterStateSnapshot
  ): Observable<boolean | UrlTree> {
    const targetUrl = state.url;

    if (this.isLoginRoute(targetUrl)) {
      return this.handleLoginRoute();
    }

    return this.ensureAuthenticated().pipe(
      switchMap(authenticated => {
        if (!authenticated) {
          this.userService.logout();
          return of(this.router.createUrlTree(['/login']));
        }
        return this.proceedWithUserCheck(state);
      })
    );
  }

  private ensureAuthenticated(): Observable<boolean> {
    if (!this.keycloak) {
      return from(this.nativeAuthService.ensureValidToken());
    }

    if (!this.keycloak.authenticated) {
      return of(false);
    }

    return from(this.keycloak.updateToken(30)).pipe(
      map(() => true),
      catchError(() => of(false))
    );
  }

  private handleLoginRoute(): Observable<boolean | UrlTree> {
    return this.ensureAuthenticated().pipe(
      switchMap(authenticated => {
        if (!authenticated) {
          this.userService.logout();
          return of(true);
        }

        return this.userService.getUserRoleFromApi().pipe(
          map(user => {
            window.localStorage.setItem('userId', user.id);
            window.localStorage.setItem('email', user.email);
            window.localStorage.setItem('ClientId', user.client?.id);
            window.localStorage.setItem('userRole', user.userRole?.id);
            this.userService.login(user?.email, user?.userRole?.id);
            return this.router.createUrlTree(this.getRoleLandingRoute(user?.userRole?.id));
          }),
          catchError(() => of(true))
        );
      })
    );
  }

  private getRoleLandingRoute(roleId: string | undefined): string[] {
    return roleId === UserRole.DISTRIBUTOR ? ['/endclients']
      : roleId === UserRole.VIVENT_ADMIN ? ['/distributors']
      : ['/trials'];
  }

  private proceedWithUserCheck(state: RouterStateSnapshot): Observable<boolean | UrlTree> {
    return this.userService.getUserRoleFromApi().pipe(
      map(role => {
        if (!role?.userRole) {
          return this.router.createUrlTree(['/login']);
        }

        const userRole = role.userRole.id;
        this.rolePermission.setCurrentRole(userRole);

        const targetUrl = state.url;
        const baseRoute = this.getBaseRoute(targetUrl);

        if (!this.rolePermission.isRouteAllowed('/' + baseRoute)) {
          return this.router.createUrlTree(['/dashboard']);
        }

        return true;
      }),
      catchError(() => of(this.router.createUrlTree(['/login'])))
    );
  }

  private getBaseRoute(url: string): string {
    return url.split('/')[1] || '';
  }

  private isLoginRoute(url: string): boolean {
    return ['/login', 'login'].includes(url);
  }
}
